import {
  AfterContentInit,
  Component,
  ContentChildren,
  QueryList,
  OnDestroy,
  ViewChild,
  ElementRef,
  Input,
  HostBinding,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { SliderItemComponent } from '../slider-item/slider-item.component';
import { Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { ColorScheme } from '@lib/models/color-scheme/color-scheme';
import {AnalyticsService} from '@utils/services/analytics/analytics.service';

@Component({
  selector: 'lib-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
})
export class SliderComponent implements AfterContentInit, OnDestroy {

  @Input() public colorScheme = ColorScheme.Dark;
  @Input() public centerItems = false;
  @Input() public fadeOut = true;
  @Input() public showButtons = true;
  @Input() public centerElementOnClick = false;
  @Input() public fadeOutColor = "#19132f";
  @Input() public moreButtonTitle = "Mehr";
  @Input() public moreButtonLink: string;
  @Input() public moreButtonRouterLink: string;
  @Input() public moreButtonSmall = false;
  public smoothScroll = true;
  public scrollStartReached = true;
  public scrollEndReached = true;
  public startGradient =
    `linear-gradient(90deg, ${this.fadeOutColor + "ff"} 0%, ${this.fadeOutColor + "00"} 100%)`;
  public endGradient =
    `linear-gradient(90deg, ${this.fadeOutColor + "00"} 0%, ${this.fadeOutColor + "ff"} 100%)`;
  @ViewChild('sliderRef') private set sliderRef(ref: ElementRef) {
    this.prepareSlider(ref);
  }
  @ContentChildren(SliderItemComponent)
  private items: QueryList<SliderItemComponent>;
  private itemChange$: Subscription;
  private slider: HTMLElement;
  private pos = { top: 0, left: 0, x: 0, y: 0 };
  private prevScrollLeft: number;
  private velX: number;
  private momentumId: number;
  private isScrolling = false;
  private itemWidth = 0;

  // Keep this to make the slider host element not draggable
  @HostBinding('attr.draggable') private draggable = false;

  constructor(@Inject(PLATFORM_ID) private platformId,
              private analyticsService: AnalyticsService) {}

  public ngAfterContentInit(): void {
    this.itemChange$ = this.items.changes.subscribe(() => {
      this.prepareItems();
    });
    this.prepareItems();
  }

  public ngOnDestroy(): void {
    this.itemChange$?.unsubscribe();
  }

  public prev(): void {
    this.analyticsService.sendPageView();
    const itemWidth = this.itemWidth + 8;
    const currentItemIndex = Math.ceil(this.slider.scrollLeft / itemWidth)
    const inViewItems = Math.floor(this.slider.clientWidth / itemWidth);
    this.scrollToItemIndex(currentItemIndex - inViewItems + 1);
  }

  public next(): void {
    this.analyticsService.sendPageView();
    const itemWidth = this.itemWidth + 8;
    const currentItemIndex = Math.ceil(this.slider.scrollLeft / itemWidth)
    const inViewItems = Math.floor(this.slider.clientWidth / itemWidth);
    this.scrollToItemIndex(currentItemIndex + inViewItems - 1);
  }

  private prepareSlider(ref: ElementRef): void {
    const element = ref.nativeElement;
    if (!element) {
      return;
    }
    this.slider = element;
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.momentumLoop = this.momentumLoop.bind(this);
    this.slider.addEventListener('mousedown', this.onMouseDown);
    this.slider.addEventListener('scroll', this.onScroll);
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    setTimeout(() => {
      this.scrollEndReached = this.slider.scrollWidth - this.slider.clientWidth === 0;
    }, 200);
  }

  private prepareItems(): void {
    let itemWidth = 0;
    this.items.forEach((item, index) => {
      const element: HTMLElement = item.ref?.nativeElement;
      if (!element) {
        return;
      }
      itemWidth += element.offsetWidth;
      this.prepareElement(element);
      if (!this.centerItems) {
        return;
      }
      if (index === 0) {
        element.style.marginLeft = `calc(50% - ${element.offsetWidth / 2}px)`;
      }
      if (index === this.items.length - 1) {
        element.style.marginRight = `calc(50% - ${element.offsetWidth / 2}px)`;
      }
    });
    this.itemWidth = Math.floor(itemWidth / this.items.length);
  }

  private scrollToItemIndex(index: number): void {
    if (index < 0) {
      index = 0;
    }
    if (index > this.items.length - 1) {
      index = this.items.length - 1;
    }
    const goToItem = this.items.get(index);
    this.scrollToItem(goToItem);
  }

  private scrollToItem(item: SliderItemComponent): void {
    if (!item) {
      return;
    }
    const element: HTMLElement = item.ref?.nativeElement;
    if (!element) {
      return;
    }
    this.smoothScroll = true;
    this.cancelMomentumTracking();
    this.slider.scrollLeft = element.offsetLeft;
  }

  private centerElement(element: HTMLElement): void {
    if (!element) {
      return;
    }
    this.cancelMomentumTracking();
    this.smoothScroll = true;
    this.slider.scrollLeft =
            element.offsetLeft - this.slider.clientWidth / 2 + element.clientWidth / 2;
  }

  private prepareElement(element: HTMLElement, canCenter = true): void {
    if (!element) {
      return;
    }
    // Prevent dragging
    element.draggable = false;
    element.addEventListener(
      'click',
      (event) => {
        // Prevent click while scrolling
        if (this.isScrolling) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        // Center element if allowed
        if (!this.isScrolling && this.centerElementOnClick && canCenter) {
          this.centerElement(element);
        }
      },
      true
    );
    if (!element.children?.length) {
      return;
    }
    Array.from(element.children).forEach((child: HTMLElement) =>
      this.prepareElement(child, false)
    );
  }

  private onScroll(): void {
    const offset = 5;
    this.scrollEndReached = this.slider.scrollLeft > this.slider.scrollWidth - this.slider.clientWidth - offset;
    this.scrollStartReached = this.slider.scrollLeft < offset;
  }

  private onMouseDown(e): void {
    this.cancelMomentumTracking();
    this.pos = {
      // The current scroll
      left: this.slider.scrollLeft,
      top: this.slider.scrollTop,
      // Get the current mouse position
      x: e.clientX,
      y: e.clientY,
    };
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private onMouseMove(e): void {
    // How far the mouse has been moved
    const dx = e.clientX - this.pos.x;
    this.prevScrollLeft = this.slider.scrollLeft;
    // Scroll the element
    this.slider.scrollLeft = this.pos.left - dx;
    this.velX = this.slider.scrollLeft - this.prevScrollLeft;
    // Threshhold before blocking element pointer events
    // Handles drag-clicking
    if (Math.abs(dx) > 5) {
      this.smoothScroll = false;
      this.isScrolling = true;
    }
  }

  private onMouseUp(): void {
    if (this.velX && this.velX !== 0) {
      this.beginMomentumTracking();
    }
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    setTimeout(() => (this.isScrolling = false), 0);
  }

  private beginMomentumTracking(): void {
    this.cancelMomentumTracking();
    this.momentumId = requestAnimationFrame(this.momentumLoop);
  }

  private cancelMomentumTracking(): void {
    cancelAnimationFrame(this.momentumId);
  }

  private momentumLoop(): void {
    this.slider.scrollLeft += this.velX;
    this.velX *= 0.97;
    if (Math.abs(this.velX) > 0.5) {
      this.momentumId = requestAnimationFrame(this.momentumLoop);
    } else {
      this.velX = 0;
      this.smoothScroll = true;
    }
  }
}
